import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';

/**
 * Wrap every `/app/:tenantSlug/...` page. The URL slug is the source of truth
 * for which tenant is active: if it belongs to one of the user's tenants and
 * differs from the current selection, switch TenantContext to it. If it
 * doesn't match any tenant the user has, bounce back to `/app` (which then
 * resolves to the default tenant via RootSlugRedirect).
 *
 * No legacy `?tenant=<uuid>` handling, no URL rewriting — `/app/{slug}/page`
 * is the only supported pattern. Old bookmarks without a slug are not
 * preserved by design.
 */
export default function TenantSlugGuard({ children }) {
  const { tenantSlug } = useParams();
  const { tenants, currentTenant, selectTenant, loading } = useTenant();

  const matchedTenant = tenants?.find((t) => t.slug === tenantSlug);
  const tenantMismatch = Boolean(matchedTenant && matchedTenant.id !== currentTenant?.id);
  const previousCommit = useRef({
    slug: tenantSlug,
    tenantId: currentTenant?.id,
    synchronized: Boolean(matchedTenant && !tenantMismatch),
    contextAhead: false,
  });
  // The menu changes context urgently while the router changes the URL in a
  // transition. Keep that selection and the shell until the URL catches up.
  // An initial tenant-list load is not a menu change from a synchronized page.
  const contextAhead = previousCommit.current.slug === tenantSlug && (
    previousCommit.current.contextAhead ||
    (previousCommit.current.synchronized && previousCommit.current.tenantId !== currentTenant?.id)
  );
  const waitingForTenant = tenantMismatch && !contextAhead;

  useLayoutEffect(() => {
    // Record commits, not speculative transition renders. Remember a pending
    // menu change across unrelated renders until its URL arrives.
    previousCommit.current = {
      slug: tenantSlug,
      tenantId: currentTenant?.id,
      synchronized: Boolean(matchedTenant && !tenantMismatch),
      contextAhead: tenantMismatch && contextAhead,
    };
  });

  useEffect(() => {
    if (waitingForTenant) {
      selectTenant(matchedTenant);
    }
  }, [matchedTenant, waitingForTenant, selectTenant]);

  // A URL-first change must wait: its page could otherwise redirect using the
  // previous tenant's accounts. Wait for initial tenant discovery as well.
  if (loading || !tenants || tenants.length === 0 || waitingForTenant) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }
  if (!matchedTenant) {
    return <Navigate to="/app" replace />;
  }
  return children;
}
